import { useEffect, useState } from 'react';
import { Check, ChevronDown, Info, Loader2, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { clock, useWaited } from './waitClock';
import { ImageLightbox } from './ImageLightbox';
import type { AskGroup } from './askPictures';
import type { AdviceContext } from './threeDApi';
import { pictureSrc, sameRef, type PictureRef } from './pictureRef';
import { modalBackdropCls, modalCardCls, wideOverlayCls } from './overlay';

// Asking for a repair: the sentence, and the pictures that go with it.
//
// ITS OWN MODAL because the question has two halves and the footer of the viewer has room
// for one. What is wrong is a description with a location in it — "the cross at the end
// nearest the thread" — and the thing it points AT is the manual's drawing. Typed into a
// three-line box under a mesh, the second half was invisible: the server picked the first
// two drawings by position and nobody saw which, so a part drawn on two pages of a manual
// was compared against whichever page came first.
//
// Now they are picked. Click a picture to send it, click it again to leave it out, and
// what is on screen IS what the model will be shown.
//
// THE PICTURES SIT ABOVE THE BOX, not under it. They are what the sentence points at, and
// you choose what you are looking at before you write about it — underneath, they read as
// an attachment to a question already asked.
//
// Escape closes THIS and not the mesh behind it, which needs the capture phase: MeshModal
// listens on the document too and would otherwise take the whole viewer away when you meant
// to dismiss a dialog. First listener down the tree wins.

export function RepairAskModal({
  instruction, onInstruction, groups, picked, onPicked, busy, onContext, contextKey,
  followingOn, refining, averageSeconds, onAsk, onClose,
}: {
  instruction: string;
  onInstruction: (v: string) => void;
  // The part's drawings, and — for a component — photographs of the product it belongs to.
  // See askPictures: what there is to show, and how many of each may go.
  groups: AskGroup[];
  picked: PictureRef[];
  onPicked: (refs: PictureRef[]) => void;
  busy: boolean;
  // What the model would be shown, fetched when somebody asks to see it. A function rather
  // than a value because it costs a Blender render, and a modal that is usually opened,
  // typed into and sent should not pay for it every time.
  onContext: () => Promise<AdviceContext>;
  // What that answer DEPENDS on, as one string: the pictures currently selected. When it
  // changes the preview is stale — the prompt counts them and names them by number.
  contextKey: string;
  // The most recent written transformation applied to this mesh, by name, or empty. Its
  // code goes into the prompt whether this is shown or not — this is what says so.
  followingOn?: string;
  // The transformation this question is a REFINEMENT of, by name, when the dialog was
  // opened from one. Empty for a fresh change.
  refining?: string;
  // What a proposal usually takes, in seconds, across every mesh. The right-hand half of
  // the clock; null means nothing has been timed yet and the clock counts alone.
  averageSeconds?: number | null;
  onAsk: () => void;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  // Whether the "what it will be shown" section is open, and what came back for it. Shut
  // by default: it is the answer to a question somebody asks once, not a thing to read
  // before every question.
  const [showing, setShowing] = useState(false);
  const [context, setContext] = useState<AdviceContext | null>(null);
  const [failed, setFailed] = useState(false);
  // Which render is open big, by its data URL — the same shape every other zoom in the app
  // takes, so the arrows step between the three.
  const [zoom, setZoom] = useState<string | null>(null);
  // Seconds since the question went out. Shared with the other button in this app that
  // waits on a model — see waitClock.
  const waited = useWaited(busy);
  // ASKED AGAIN WHENEVER THE PICTURES CHANGE, because the prompt says how many there are
  // and which is which: unselect the drawing and "PICTURE 1 is the manual drawing" becomes
  // false, which is exactly the sentence somebody opened this to check.
  //
  // The renders behind it cost a cold Blender, so the server caches them on the file — the
  // mesh has not moved between one click and the next, and only the wording has.
  //
  // eslint-disable-next-line react-hooks/exhaustive-deps -- onContext is rebuilt every
  // render by its caller; `contextKey` is what actually changed and is what this follows.
  useEffect(() => {
    let alive = true;
    setContext(null);
    setFailed(false);
    void onContext()
      .then((got) => alive && setContext(got))
      // Said quietly, in the block where the pictures would have been. It used to be
      // swallowed, back when this only fed a disclosure nobody had opened: now the mesh's
      // own renders live in that block, and silence there reads as "this mesh has none".
      .catch(() => alive && setFailed(true));
    return () => { alive = false; };
  }, [contextKey]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      // The zoom first, if one is open. This listener is on the capture phase and would
      // otherwise take the whole dialog away when somebody meant to shut a picture — the
      // lightbox's own Escape never gets the event.
      if (zoom) setZoom(null);
      else onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose, zoom]);

  const toggle = (ref: PictureRef) =>
    onPicked(picked.some((p) => sameRef(p, ref))
      ? picked.filter((p) => !sameRef(p, ref))
      : [...picked, ref]);

  const nothingToShow = groups.every((g) => !g.refs.length);

  return (
    // Not the shared overlayCls, which pads left of the desktop sidebar so a dialog
    // centres in the app's content area. This one opens INSIDE the mesh modal, which
    // centres in the window — the same offset applied twice would sit it off to the right
    // of the thing it belongs to.
    <div
      className={`${wideOverlayCls} ${modalBackdropCls}`}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className={`relative max-h-[85vh] w-full max-w-2xl overflow-y-auto p-5 ${modalCardCls}`}
      >
        <h2 className="mb-1 text-center text-base font-semibold text-slate-900">
          {t('notice.3d.askTitle')}
        </h2>
        {/* WHAT THE LAST CHANGE WAS, when there was one. The next question is usually about
            it — "thread the hole you just cut" — and the model is given that change's own
            code to read, which is where the hole it made actually is. Said here so the
            person asking knows they can refer to it instead of describing it again. */}
        {refining ? (
          /* WHAT THIS QUESTION IS ABOUT, when it was opened from a proposal. Louder than
             the line below it because it changes what the sentence means: "make it 2 mm
             deeper" is a correction to one named thing, and the model is told which and
             handed the code that made it. */
          <p className="mb-3 text-center text-xs text-blue-700">
            {t('notice.3d.refining')} <span className="font-semibold">“{refining}”</span>
          </p>
        ) : followingOn && (
          <p className="mb-3 text-center text-xs text-slate-500">
            {t('notice.3d.followingOn')} <span className="font-semibold">{followingOn}</span>
          </p>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 rounded-lg p-1 text-slate-400 transition-colors hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>

        {nothingToShow ? (
          <p className="mb-3 text-center text-xs text-amber-600">
            {t('notice.3d.askNoDrawings')}
          </p>
        ) : (
          groups.filter((g) => g.refs.length).map((g) => {
            const full = picked.filter((p) => p.source === g.refs[0].source).length >= g.max;
            return (
              <div key={g.kind} className="mb-4">
                <p className="mb-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t(g.kind === 'schematic' ? 'notice.3d.askDrawings' : 'notice.3d.askProduct')}
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  {g.refs.map((ref) => {
                    const on = picked.some((p) => sameRef(p, ref));
                    return (
                      <button
                        key={`${ref.source}-${ref.imageId}`}
                        type="button"
                        onClick={() => toggle(ref)}
                        // Nothing is greyed out for being unselected — every picture here
                        // is one you might send. What a full selection can no longer do is
                        // add one more, and that is what the tooltip has to say.
                        disabled={!on && full}
                        title={!on && full ? t('notice.3d.askMaxPictures') : undefined}
                        className={`relative block rounded-lg border-2 bg-white p-1 transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                          on
                            ? 'border-blue-500'
                            : 'border-slate-200 hover:border-blue-400'
                        }`}
                      >
                        <img src={pictureSrc(ref)} alt="" className="h-24 w-auto rounded" />
                        {on && (
                          <span className="absolute right-1 top-1 rounded-full bg-blue-500 p-0.5 text-white">
                            <Check className="h-3 w-3" />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}

        {/* THE MESH ITSELF, in the same row of pictures as the drawings — because it is one
            of the pictures, and it went unmentioned up here while three of them went with
            every call. Marked as chosen, and it cannot be unchosen: a proposal about a mesh
            nobody let the model look at is a proposal about a sentence.

            Its three views are the vocabulary the answer has to be written in: every repair
            acts on one END, and 'min'/'max' mean nothing until you have seen which is
            which. */}
        {/* THE BLOCK IS ALWAYS HERE, even before the renders are. They take a cold Blender
            the first time a mesh is asked about, and a section that appears two seconds
            late reads as one that was not going to appear at all — which is the opposite
            of what it is for. */}
        <div className="mb-4">
          <p className="mb-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('notice.3d.askRenders')}
          </p>
          {!context ? (
            <div className="flex justify-center py-4">
              {failed ? (
                <p className="text-center text-xs text-amber-600">
                  {t('notice.3d.askRendersFailed')}
                </p>
              ) : (
                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              )}
            </div>
          ) : (
            <div className="flex flex-wrap justify-center gap-3">
              {context.views.map((v) => (
                <button
                  key={v.name}
                  type="button"
                  // Clicking ZOOMS, where clicking a drawing above selects it. Not an
                  // inconsistency: these three cannot be unselected, so the click is free
                  // for the thing you actually want from a 96px render — a closer look at
                  // the end you are about to name.
                  onClick={() => setZoom(v.dataUrl)}
                  title={t('notice.3d.askRendersHint')}
                  className="relative flex flex-col items-center gap-0.5 rounded-lg border-2 border-blue-500 bg-white p-1 transition-opacity hover:opacity-90"
                >
                  <img src={v.dataUrl} alt={v.name} className="h-24 w-24 rounded object-contain" />
                  <span className="absolute right-1 top-1 rounded-full bg-blue-500 p-0.5 text-white">
                    <Check className="h-3 w-3" />
                  </span>
                  <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                    {v.name}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Cmd/Ctrl+Enter sends and Enter makes a newline, as it did in the box this
            replaces. The other way round is the chat convention and is wrong here: what
            sits on the far side of a mistaken keystroke is a paid call taking half a
            minute. */}
        <textarea
          value={instruction}
          onChange={(e) => onInstruction(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onAsk();
          }}
          autoFocus
          rows={5}
          placeholder={t('notice.3d.askWhatsWrong')}
          className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-center text-sm leading-snug text-slate-800 outline-none placeholder:text-slate-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40"
        />

        {/* THE OTHER HALF OF WHAT IS SENT, and until now the invisible half. The pictures
            above are chosen here, so they were never in doubt; the three renders of the
            mesh, its measurements and the prompt itself went with every call and could be
            read only in the source.

            Fetched from the server, by the same code that composes the real call — a
            preview assembled separately would agree with it today and drift on the first
            change to either. It costs a Blender render and no model call, which is why it
            is asked for once when this opens rather than on every keystroke. */}
        <div className="mt-4 rounded-lg border border-slate-200 p-2">
          <button
            type="button"
            onClick={() => setShowing((v) => !v)}
            className="flex w-full items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 transition-colors hover:text-blue-600"
          >
            <Info className="h-3.5 w-3.5" />
            {t('notice.3d.askShown')}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showing ? 'rotate-180' : ''}`} />
          </button>

          {showing && (
            <div className="mt-2 flex flex-col items-center gap-2">
              {!context ? (
                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              ) : (
                /* The prompt as it will be, with the sentence spliced in where it goes —
                   live, so what is read is what would be sent if the button were pressed
                   now. The pictures are above, chosen; this is the words. The measurements
                   are inside it, and printing them twice would be two answers to one
                   question. */
                <pre className="max-h-48 w-full overflow-auto whitespace-pre-wrap rounded border border-slate-200 bg-slate-50 p-2 text-[10px] leading-snug text-slate-600">
                  {context.prompt.replace(context.slot, instruction || '…')}
                </pre>
              )}
            </div>
          )}
        </div>

        <ImageLightbox
          src={zoom}
          srcs={(context?.views || []).map((v) => v.dataUrl)}
          onNavigate={setZoom}
          onClose={() => setZoom(null)}
        />

        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={onAsk}
            disabled={busy || !instruction.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('notice.3d.askRepair')}
            {/* HOW LONG IT HAS BEEN, against how long it usually takes. Half a minute of a
                spinner is a page that looks stuck; the same wait with a number it is
                heading for is a wait. The second half is the average across every mesh —
                one mesh's two samples is not an estimate — and is left off entirely until
                something has been timed, rather than shown as zero. */}
            {busy && (
              <span className="tabular-nums text-xs font-normal opacity-80">
                ({clock(waited)}{averageSeconds ? ` / ${clock(averageSeconds)}` : ''})
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
